/**
 * Offline-first IndexedDB layer for Dispatch & Logistics (Phase 5).
 *
 * Two purposes:
 *   1. CACHE — locked dispatch labels + bundle summaries for an
 *      (periodical_id, issue_month_year) tuple so labels can be printed
 *      with zero network.
 *   2. QUEUE — pending mutations (RTO scans, tracking-import rows) made
 *      while offline. The sync worker drains this queue when the
 *      browser fires the `online` event.
 *
 * Implementation: Dexie.js (thin wrapper around IndexedDB).
 */
import Dexie from "dexie";

export const db = new Dexie("AdminDashboardOffline");

db.version(1).stores({
  // Primary key is a composite string `${periodical_id}::${issue_month_year}`.
  dispatch_cache: "key, periodical_id, issue_month_year, cached_at",
  // Auto-increment id; type discriminates the worker behaviour.
  sync_queue: "++id, type, status, created_at",
});

// Tiny pub/sub so any UI component (e.g. the Topbar `OfflineStatus` pill) can
// re-hydrate immediately whenever we mutate the sync_queue table — without
// pulling in a global state library.
const queueListeners = new Set();
export function onQueueChanged(cb) {
  queueListeners.add(cb);
  return () => queueListeners.delete(cb);
}
function emitQueueChanged() {
  queueListeners.forEach((cb) => { try { cb(); } catch {} });
}

const cacheKey = (perId, issue) => `${perId}::${issue}`;

/* ---------- Dispatch cache ---------- */
export async function cacheLabels(periodicalId, issue, items, meta = {}) {
  if (!periodicalId || !issue || !Array.isArray(items)) return;
  await db.dispatch_cache.put({
    key: cacheKey(periodicalId, issue),
    periodical_id: periodicalId,
    issue_month_year: issue,
    items,
    meta,
    cached_at: new Date().toISOString(),
  });
}

export async function getCachedLabels(periodicalId, issue) {
  if (!periodicalId || !issue) return null;
  return await db.dispatch_cache.get(cacheKey(periodicalId, issue));
}

export async function listAllCaches() {
  return await db.dispatch_cache.orderBy("cached_at").reverse().toArray();
}

export async function deleteCache(periodicalId, issue) {
  return await db.dispatch_cache.delete(cacheKey(periodicalId, issue));
}

/* ---------- Sync queue ---------- */
/**
 * @param {("rto"|"tracking")} type
 * @param {object} payload  — exact body to POST to the API later
 * @param {object} [meta]   — display-only context (e.g. subscriber name, count)
 */
export async function enqueue(type, payload, meta = {}) {
  const id = await db.sync_queue.add({
    type,
    payload,
    meta,
    status: "pending",
    attempts: 0,
    last_error: null,
    created_at: new Date().toISOString(),
  });
  emitQueueChanged();
  return id;
}

export async function listQueue(status = null) {
  let q = db.sync_queue.orderBy("created_at");
  if (status) q = q.filter((r) => r.status === status);
  return await q.toArray();
}

export async function pendingCount() {
  return await db.sync_queue.where("status").equals("pending").count();
}

export async function markSynced(id) {
  await db.sync_queue.delete(id);
  emitQueueChanged();
}

export async function markFailed(id, err) {
  const row = await db.sync_queue.get(id);
  if (!row) return;
  await db.sync_queue.update(id, {
    attempts: (row.attempts || 0) + 1,
    last_error: String(err).slice(0, 400),
    status: (row.attempts || 0) + 1 >= 5 ? "failed" : "pending",
  });
  emitQueueChanged();
}

export async function clearQueue() {
  await db.sync_queue.clear();
  emitQueueChanged();
}

export default db;
